<template>
    <div class="malt-datepicker_root" :class="{'malt-datepicker_root--inline': inline}">
        <template v-if="inline">
            <VJoyLabel v-if="label">{{ label }}</VJoyLabel>
            <DatePicker
                :key="datePickerKey"
                v-bind="{disabled, minDate, maxDate, disabledDays, disabledDates, selectionMode}"
                :number-of-months="computedNumberOfMonths"
                inline
                :view="computedDatePickerView"
                show-button-bar
                fluid
                :data-testid="resolvedDatePickerTestid"
                class="malt-datepicker_calendar"
                :data-multiple-months="computedNumberOfMonths > 1"
                :show-time="view === 'date-time'"
                :model-value="datePickerValue"
                hour-format="24"
                @update:model-value="handleDatePickerChange"
            />
        </template>

        <template v-else>
            <MaltInputMask
                ref="input-mask"
                v-bind="{disabled, modelValue, dataTestid, label, ...props, ...$attrs}"
                icon="calendar"
                :model-value="formatedDate"
                inputmode="numeric"
                type="text"
                :preset="computedPreset"
                @focus="handleInputFocus"
                @click="handleInputClick"
                @update:model-value="handleInputValueChange"
                @keydown.esc.stop="closeDatePicker"
                @keydown.tab="closeDatePicker"
                @keydown.down="openDatePicker"
            >
                <slot />
            </MaltInputMask>

            <ClientOnly>
                <Teleport to="#teleports">
                    <div
                        ref="floatingRef"
                        tabindex="-1"
                        class="malt-datepicker_floating"
                        :style="floatingStyles"
                        data-click-outside-ignore
                        @keydown.esc.stop="closeDatePicker"
                        @click.stop
                    >
                        <Transition name="fade-scale" mode="out-in">
                            <DatePicker
                                v-if="isPositioned"
                                :key="datePickerKey"
                                v-bind="{disabled, minDate, maxDate, disabledDays, disabledDates, selectionMode}"
                                :number-of-months="computedNumberOfMonths"
                                inline
                                :data-placement="placement"
                                :view="computedDatePickerView"
                                show-button-bar
                                fluid
                                :data-testid="resolvedDatePickerTestid"
                                class="malt-datepicker_calendar"
                                :data-multiple-months="computedNumberOfMonths > 1"
                                :show-time="view === 'date-time'"
                                :model-value="datePickerValue"
                                hour-format="24"
                                @update:model-value="handleDatePickerChange"
                            />
                        </Transition>
                    </div>
                </Teleport>
            </ClientOnly>
        </template>
    </div>
</template>

<script
    setup
    lang="ts"
    generic="
        TSelectionMode extends 'single' | 'range' = 'single',
        TValueType extends Date | Date[] | undefined | null = (TSelectionMode extends 'single' ? Date : Date[]) | undefined
    "
>
    import {computed, onMounted, ref, useTemplateRef, watch, type ComponentPublicInstance} from 'vue';
    import {flip, offset} from '@floating-ui/dom';
    import {autoUpdate, shift, useFloating} from '@floating-ui/vue';
    import {onClickOutside, type Fn} from '@vueuse/core';
    import {isSameDay} from 'date-fns';
    import {toZonedTime} from 'date-fns-tz';
    import DatePicker, {type DatePickerProps} from 'primevue/datepicker';
    import {useLogger} from '#imports';
    import {useIntersectionObserver, useMediaQuery} from '@malt/utils';
    import {VJoyLabel} from '@maltjoy/core-vue';
    import {useSetPrimevueLocales, useDateFormatters} from '../common';
    import MaltInputMask, {type MaltInputMaskPreset, type MaltInputMaskProps} from '../MaltInputMask/MaltInputMask.vue';

    export type MaltDatePickerView = 'date' | 'month' | 'year' | 'date-time';

    useSetPrimevueLocales();

    interface MaltDatePickerProps extends Omit<MaltInputMaskProps, 'modelValue' | 'ref' | 'type' | 'maskOptions' | 'type'> {
        minDate?: Date;
        maxDate?: Date;
        /**
         * Days of the week that cannot be selected (0 = Sunday, 6 = Saturday)
         */
        disabledDays?: number[];
        /**
         * Specific dates that cannot be selected
         */
        disabledDates?: Date[];
        /**
         * Apply a data-testid to the datepicker popin
         */
        datePickerTestid?: string;
        /**
         * Switch PrimeVue datepicker view
         *
         * @default 'date'
         */
        view?: MaltDatePickerView;
        /**
         * Switch selection mode between single and range
         *
         * @default 'single'
         */
        selectionMode?: TSelectionMode;
        /**
         * If true, only the input will be displayed, without the datepicker
         *
         * @default false
         */
        inputOnly?: boolean;
        /**
         * If true, the calendar is displayed inline without the input field and floating popup
         *
         * @default false
         */
        inline?: boolean;
        /**
         *  Number of months to display
         *
         * @default 1
         */
        numberOfMonths?: number;
    }

    const {
        view = 'date',
        selectionMode = 'single',
        disabled,
        inputOnly = false,
        inline = false,
        label,
        datePickerTestid,
        dataTestid,
        numberOfMonths = 1,
        ...props
    } = defineProps<MaltDatePickerProps>();

    const modelValue = defineModel<TValueType>();

    // ------ Utils
    const logger = useLogger();
    const {formatZoneDateToString, dateToZonedTime, parseValueFromTimezone} = useDateFormatters();
    const {matchMedia} = useMediaQuery();

    // ------ Doms references

    /** Reference to the MaltInputMask component
     * Types not working with generic components so it can't be referenced directly
     */
    const inputMaskRef = useTemplateRef<ComponentPublicInstance>('input-mask');
    /** Input element is the reference for the datepicker popin, otherwise the label is adding padding */
    const inputElementRef = ref<HTMLInputElement>();
    /** Datepicker container reference */
    const floatingRef = useTemplateRef('floatingRef');

    onMounted(() => {
        if (inputMaskRef.value) {
            // Get the child input element of the VJoyInput component, to avoid the label being used as a reference for the floating datepicker
            const joyInputWrapper = inputMaskRef.value.$el.querySelector('.joy-input--wrapper input') as HTMLInputElement | undefined;
            if (joyInputWrapper) {
                inputElementRef.value = joyInputWrapper;
            } else {
                logger.warn('[MaltDatePicker] No Joy input element found');
            }
        }
    });

    // ------ Formatting from the model value

    /** Convert the date to a compatible format displayed in the input */
    const formatedDate = computed<string>(() => {
        if (!modelValue.value) {
            return '';
        }

        if (Array.isArray(modelValue.value)) {
            return modelValue.value.map((date) => formatZoneDateToString(date, view)).join(' - ');
        } else if (modelValue.value instanceof Date) {
            return formatZoneDateToString(modelValue.value, view);
        }
        return modelValue.value;
    });

    // ------ Formatting the value passed to the datepicker

    /** Converts the Date to a Timezone-aware Date to display in the datepicker */
    const datePickerValue = computed<Date | Date[] | undefined | null>(() => {
        if (!modelValue.value) {
            return modelValue.value === null ? null : undefined;
        }

        if (Array.isArray(modelValue.value)) {
            return modelValue.value.map(dateToZonedTime).filter((date): date is Date => !!date);
        }
        return dateToZonedTime(modelValue.value);
    });

    /** Workaround for PrimeVue bug: forces remount when decade/month/year changes otherwise the calendar is not updated when filled manually */
    const datePickerKey = computed(() => {
        if (!datePickerValue.value || Array.isArray(datePickerValue.value)) {
            return 'empty';
        }
        if (view === 'year') {
            return `decade-${Math.floor(datePickerValue.value.getFullYear() / 10)}`;
        }
        if (view === 'month') {
            return `year-${datePickerValue.value.getFullYear()}`;
        }
        return `month-${datePickerValue.value.getFullYear()}-${datePickerValue.value.getMonth()}`;
    });

    // ------ Common props

    const computedDatePickerView = computed<DatePickerProps['view']>(() => {
        if (view === 'date-time') {
            return 'date';
        }
        return view;
    });

    const computedPreset = computed<Extract<MaltInputMaskPreset, 'date' | 'date-range' | 'date-month-year' | 'date-year' | 'date-time'>>(() => {
        if (selectionMode !== 'single') return 'date-range';

        switch (view) {
            case 'month':
                return 'date-month-year';
            case 'year':
                return 'date-year';
            case 'date-time':
                return 'date-time';
            default:
                return 'date';
        }
    });

    const computedNumberOfMonths = computed(() => {
        if (matchMedia.value.max.Xs) {
            return 1;
        }
        return numberOfMonths;
    });

    const resolvedDatePickerTestid = computed(() => datePickerTestid || `${dataTestid}-datepicker`);

    // ------ Floating datepicker events

    const isOpened = ref(false);
    const isHiddenFromScroll = ref(false);

    const computedIsOpened = computed<boolean>(() => isOpened.value && !isHiddenFromScroll.value);

    const {floatingStyles, isPositioned, placement} = useFloating(inputElementRef, floatingRef, {
        whileElementsMounted: autoUpdate,
        placement: 'bottom-start',
        middleware: [
            flip({
                crossAxis: 'alignment',
                fallbackPlacements: ['top-start', 'right-start', 'left-start'],
                fallbackStrategy: 'initialPlacement',
                padding: 20,
            }),
            shift({
                padding: 4,
            }),
            offset({
                mainAxis: 4,
                crossAxis: 10,
                alignmentAxis: 4,
            }),
        ],
        open: computedIsOpened,
    });

    const {startObserving, stopObserving} = useIntersectionObserver(
        inputMaskRef,
        (entries) => {
            // Allows to hide the datepicker when input is not visible completely, and to show it back again when it's fully visible
            if (entries[0] && entries[0].intersectionRatio < 1 && isOpened.value) {
                hideDatePickerFromScroll();
            } else if (entries[0]?.intersectionRatio === 1 && isHiddenFromScroll.value) {
                showDatePickerFromScroll();
            }
        },
        {
            immediate: false,
        },
    );

    // Prove to prevent double click event propagation from "@focus" and "@click"
    const isClickEventPropagating = ref(false);
    let isClickEventPropagatingTimeout: ReturnType<typeof setTimeout> | undefined;

    let disableClickOutside: Fn | undefined;

    function handleInputFocus(event: FocusEvent) {
        disableClickOutside?.();
        clearTimeout(isClickEventPropagatingTimeout);
        isClickEventPropagating.value = true;
        event?.stopPropagation();
        handleAllFocus();
        isClickEventPropagatingTimeout = setTimeout(() => {
            isClickEventPropagating.value = false;
            registerClickOutside();
        }, 200);
    }

    function handleInputClick(event: MouseEvent) {
        if (isClickEventPropagating.value) {
            event.preventDefault();
            event.stopPropagation();
        } else {
            if (isClickEventPropagatingTimeout) {
                clearTimeout(isClickEventPropagatingTimeout);
                isClickEventPropagatingTimeout = undefined;
            }
            registerClickOutside();
            handleAllFocus();
        }
    }

    function handleAllFocus() {
        if (!disabled && !isOpened.value) {
            startObserving();
            openDatePicker();
        }
    }

    function registerClickOutside() {
        disableClickOutside = onClickOutside(floatingRef, closeDatePicker, {
            ignore: [inputElementRef],
        });
    }

    watch(
        () => disabled,
        (newDisabled) => {
            if (newDisabled) {
                closeDatePicker();
            }
        },
    );

    function openDatePicker() {
        if (!disabled && !inputOnly) {
            isOpened.value = true;

            startObserving();
        }
    }

    function closeDatePicker() {
        disableClickOutside?.();
        stopObserving();
        showDatePickerFromScroll();
        isOpened.value = false;
    }

    function hideDatePickerFromScroll() {
        isHiddenFromScroll.value = true;
    }

    function showDatePickerFromScroll() {
        isHiddenFromScroll.value = false;
    }

    // ------ Input events

    function handleDatePickerChange(value?: Date | Date[] | (Date | null)[] | null | undefined) {
        let hasTimeChanged = false;
        if (view === 'date-time' && value != null && !Array.isArray(modelValue.value) && !Array.isArray(value) && value instanceof Date) {
            if (modelValue.value && modelValue.value instanceof Date) {
                // PrimeVue Datepicker has the same event when selecting a date or changing the time
                // The popin closes everytime we modify the time manually
                // This workaround fixes the problem by checking if the date is the same
                hasTimeChanged = isSameDay(toZonedTime(modelValue.value, 'UTC'), value);
            } else {
                // In purpose if previous value is undefined, prevent closing the datepicker before chosing time
                hasTimeChanged = true;
            }
        }
        try {
            if (Array.isArray(value)) {
                handleValueChange(value.filter((v) => v instanceof Date) as Date[]);
            } else {
                handleValueChange(value ?? (modelValue.value === null ? null : undefined));
            }
        } finally {
            if (!inline && !Array.isArray(value) && !hasTimeChanged) {
                isOpened.value = false;
            }
        }
    }

    function handleInputValueChange(value: string | number | undefined) {
        if (typeof value === 'number') {
            value = value.toString() as string;
        }
        handleValueChange(value);
    }

    function handleValueChange(value?: string | string[] | Date | Date[] | undefined | null | `${string} - ${string}`) {
        if (!value) {
            if (Array.isArray(modelValue.value)) {
                modelValue.value = [] as unknown as TValueType;
            } else {
                // Keep the null value if the model value is null, otherwise set to undefined
                // This can trigger a non-wanted early validation error as the value changed
                modelValue.value = (modelValue.value === null ? null : undefined) as TValueType;
            }
        } else if (Array.isArray(value)) {
            modelValue.value = value.filter((date) => !!date).map((date) => parseValueFromTimezone(date, view)) as TValueType;
        } else if (typeof value === 'string' && value.includes(' - ')) {
            modelValue.value = value
                .split(' - ')
                .map((date) => parseValueFromTimezone(date, view))
                .filter((date): date is Date => !!date) as TValueType;
        } else {
            modelValue.value = parseValueFromTimezone(value, view) as TValueType;
        }
    }
</script>

<style lang="scss" scoped>
    // DatePicker appear and hide transition
    .fade-scale-enter-active,
    .fade-scale-leave-active {
        transition-property: opacity transform;
        transition-duration: 0.15s;
        transition-timing-function: ease;
    }
    .fade-scale-leave-active {
        transition-duration: 0s;
    }

    .fade-scale-enter-to {
        transform: scale(1);
    }

    .fade-scale-enter-from,
    .fade-scale-leave-to {
        opacity: 0;

        &[data-placement^='top'] {
            transform-origin: bottom left;
        }

        &[data-placement^='bottom'] {
            transform-origin: top left;
        }

        transform: scale(0.95);
    }

    // DatePicker floating container
    // z-index is above dialogs and below tooltips
    .malt-datepicker_floating {
        z-index: var(--joy-core-z-index-tooltip);

        .malt-datepicker_calendar:not([data-multiple-months='true']) {
            max-width: 305px;
        }
    }

    .malt-datepicker_calendar {
        --p-datepicker-panel-border-radius: var(--joy-core-radius-4);
        // Selected date
        --p-datepicker-date-selected-background: var(--joy-color-primary-50);
        --p-datepicker-today-background: var(--joy-color-neutral-20);
        --p-datepicker-today-color: var(--joy-color-primary-30);

        // Selected range
        --p-datepicker-date-range-selected-background: var(--joy-color-primary-10);
        --p-datepicker-date-range-selected-color: var(--joy-color-primary-50);

        // Hovered date
        --p-datepicker-date-hover-background: var(--joy-color-neutral-0);
        --p-datepicker-dropdown-hover-border-color: var(--joy-color-primary-50);

        :deep(.p-datepicker-day) {
            font-weight: var(--joy-font-weight-bold);
        }

        :deep(.p-datepicker-day:not(.p-datepicker-day-selected):not(.p-disabled):hover) {
            border-color: var(--joy-color-primary-50);
            border-width: var(--joy-form-border-width);
        }

        :deep(.p-datepicker-panel) {
            box-shadow: var(--joy-core-elevation-3) !important;
        }
    }

    .malt-datepicker_root--inline {
        .malt-datepicker_calendar {
            :deep(.p-datepicker-panel) {
                box-shadow: none !important;
            }
        }
    }
</style>
